import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type KeyboardEvent,
  type RefObject,
  type SetStateAction,
} from 'react';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { extractCaseId, filterCases } from '../lib/search-helpers';
import { navigateToSearchResult } from '../lib/navigate-search-result';
import type { SearchResult } from '../lib/search-types';
import { useMyCases } from './use-cases';
import { useSearch } from './use-search';

function useOutsideClick(
  ref: RefObject<HTMLElement | null>,
  enabled: boolean,
  onOutside: () => void,
) {
  useEffect(() => {
    if (!enabled) return;
    function onPointerDown(e: MouseEvent) {
      const el = ref.current;
      if (el && !el.contains(e.target as Node)) onOutside();
    }
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [enabled, onOutside, ref]);
}

function useSearchResults(query: string, caseId: string | null) {
  const { results: searchResults, isSearching, isConfigured } = useSearch(query, caseId);
  const { data: myCases } = useMyCases();
  const caseResults = useMemo(() => filterCases(myCases, query), [myCases, query]);
  // Alltid inkluder sakstreff; Typesense dekker ikke sakslisten.
  const results = useMemo(() => {
    if (!isConfigured) return caseResults;
    return [...caseResults, ...searchResults];
  }, [caseResults, isConfigured, searchResults]);
  return { results, isSearching, isConfigured };
}

function useResultKeyboard(
  showDropdown: boolean,
  results: SearchResult[],
  activeIndex: number,
  setActiveIndex: Dispatch<SetStateAction<number>>,
  close: () => void,
  selectResult: (result: SearchResult) => void,
) {
  return useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
        return;
      }
      if (!showDropdown || results.length === 0) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((i) => (i + 1) % results.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((i) => (i - 1 + results.length) % results.length);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const active = results[activeIndex];
        if (active) selectResult(active);
      }
    },
    [activeIndex, close, results, selectResult, setActiveIndex, showDropdown],
  );
}

export function useTopbarSearch() {
  const [query, setQueryRaw] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { results, isSearching, isConfigured } = useSearchResults(query, extractCaseId(pathname));
  const showDropdown = isOpen && Boolean(query.trim());

  useEffect(() => setActiveIndex(0), [results]);

  const close = useCallback(() => {
    setIsOpen(false);
    setActiveIndex(0);
  }, []);

  const clear = useCallback(() => {
    setQueryRaw('');
    setIsOpen(false);
    setActiveIndex(0);
  }, []);

  const selectResult = useCallback(
    (result: SearchResult) => {
      clear();
      navigateToSearchResult(navigate, result);
    },
    [clear, navigate],
  );

  useOutsideClick(containerRef, showDropdown, close);
  const onKeyDown = useResultKeyboard(
    showDropdown,
    results,
    activeIndex,
    setActiveIndex,
    close,
    selectResult,
  );

  const setQuery = useCallback((q: string) => {
    setQueryRaw(q);
    setActiveIndex(0);
    setIsOpen(true);
  }, []);

  return {
    query,
    setQuery,
    results,
    isSearching,
    isConfigured,
    activeIndex,
    setActiveIndex,
    showDropdown,
    containerRef,
    onKeyDown,
    onFocus: () => {
      if (query.trim()) setIsOpen(true);
    },
    selectResult,
    clear,
  };
}
